import { useEffect, useState } from "react"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import { Modal, ETypeOfNotification, setNotification, Combobox, RadioButtonGroup, InputSwitch, InputTextCustom } from "medere-ui-framework"
import { useDispatch } from "react-redux"
import "./FavoriteStudyModal.scss"
import { findFavoriteStudyByDescription, getStudyTypes, getApiErrorInfo, getApiErrorMessage, FavouriteStudyFormItemState, FullFavouriteStudyDTO, isNbuItem, mapFullFavouriteStudyToForm, resolveFavouriteStudyId, saveFullFavouriteStudy, toSaveFullFavouriteStudyPayload } from "../../servicesHooks/FavoriteStudies/useFavoriteStudiesService"
import { searchTerminologySnomed } from "../../servicesHooks/Terminology/useTerminologyService"
import { EEntityTag } from "../../enums/EEntityTag"
import { useNomenclatorService } from "../../servicesHooks/Nomenclator/useNomenclatorService"

interface StudyTypeOptionSource {
  studyType: number
  i18ncodes?: Array<{ description: string }>
}

interface NomenclatorOptionSource {
  code?: string
  shortdescription?: string
  description?: string
}

interface NomenclatorSelectOption {
  label: string
  value: NomenclatorOptionSource
}

const isItemCompatibleWithSource = (
  item: FavouriteStudyFormItemState,
  sourceId: 1 | 2 | null
): boolean => {
  if (!sourceId) return true
  return sourceId === 1 ? isNbuItem(item) : !isNbuItem(item)
}

const normalize = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

const deriveSourceIdFromStudyType = (
  studyType: StudyTypeOptionSource | null
): 1 | 2 | null => {
  if (!studyType) return null
  const description = studyType.i18ncodes?.[0]?.description ?? ""
  return normalize(description).includes("laboratorio") ? 1 : 2
}

interface Props {
  visible: boolean
  setVisible: (v: boolean) => void
  onSaved?: () => void
  studyToEdit?: FullFavouriteStudyDTO | null
}

export default function CRUDFavoriteStudyModal({
  visible,
  setVisible,
  onSaved,
  studyToEdit
}: Props) {
  const dispatch = useDispatch()
  const { getNomenclatorCodes } = useNomenclatorService()
  const settings = JSON.parse(localStorage.getItem("settings") ?? "{}")
  const loggedUserTags: number[] = Array.isArray(settings?.loginResponse?.user?.tags)
    ? settings.loginResponse.user.tags.map((tag: unknown) => Number(tag)).filter((tag: number) => Number.isFinite(tag))
    : []
  const isAdministrativeOnlyUser =
    loggedUserTags.includes(EEntityTag.ADMINISTRATIVE) &&
    !loggedUserTags.includes(EEntityTag.PHYSICIAN)

  const [form, setForm] = useState({
    favouritestudy: 0,
    favouritestudydesc: "",
    studytype: 0,
    active: true,
    ispublic: isAdministrativeOnlyUser,
    usedInAppointments: false,
    usedInStudyOrders: false
  })

  const [studyTypes, setStudyTypes] = useState<StudyTypeOptionSource[]>([])

  // Texto libre deshabilitado temporalmente.
  // Se conserva comentado por si en el futuro vuelve a habilitarse.
  // const [freeText, setFreeText] = useState("")
  // const [origin, setOrigin] = useState<"nomenclador" | "libre">("nomenclador")
  // const [freeCode, setFreeCode] = useState("")

  const [selectedStudy, setSelectedStudy] =
    useState<NomenclatorSelectOption | null>(null)
  const [addedStudies, setAddedStudies] = useState<FavouriteStudyFormItemState[]>(
    []
  )
  const [removedStudyItemIds, setRemovedStudyItemIds] = useState<number[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    getStudyTypes().then(setStudyTypes)
  }, [])

  useEffect(() => {
    if (studyToEdit) {
      const mapped = mapFullFavouriteStudyToForm(studyToEdit)
      setForm({
        ...mapped.form,
        ispublic: isAdministrativeOnlyUser ? true : mapped.form.ispublic
      })
      setAddedStudies(mapped.items)
      setRemovedStudyItemIds([])
    } else {
      setForm({
        favouritestudy: 0,
        favouritestudydesc: "",
        studytype: 0,
        active: true,
        ispublic: isAdministrativeOnlyUser,
        usedInAppointments: false,
        usedInStudyOrders: false
      })
      setAddedStudies([])
      setRemovedStudyItemIds([])
    }
  }, [isAdministrativeOnlyUser, studyToEdit])

  const selectedStudyTypeOption =
    studyTypes
      .map(t => ({
        label: t.i18ncodes?.[0]?.description ?? `Tipo ${t.studyType}`,
        value: Number(t.studyType)
      }))
      .find(o => o.value === Number(form.studytype)) || null

  const selectedStudyType =
    studyTypes.find(item => Number(item.studyType) === Number(form.studytype)) ??
    null

  const sourceId = deriveSourceIdFromStudyType(selectedStudyType)

  useEffect(() => {
    setSelectedStudy(null)
    setAddedStudies(prev =>
      prev.filter(item => isItemCompatibleWithSource(item, sourceId))
    )
  }, [sourceId, form.studytype])

  const handleAddStudy = () => {
    // Texto libre deshabilitado temporalmente.
    // Si se vuelve a habilitar, revisar validaciones, agregado de ítems
    // y guardado del payload antes de reactivar esta lógica.
    //
    // if (origin === "libre") {
    //   if (!freeCode.trim() || !freeText.trim()) return
    //
    //   setAddedStudies(prev => [
    //     ...prev,
    //     {
    //       favouritestudyitem: null,
    //       nomenclatorid: null,
    //       nomenclatorcode: null,
    //       nomenclatordescription: null,
    //       code: freeCode.trim(),
    //       description: freeText.trim()
    //     }
    //   ])
    //
    //   setFreeCode("")
    //   setFreeText("")
    //   return
    // }

    if (!selectedStudy) return
    if (!sourceId) return

    const n = selectedStudy.value
    const isNbu = sourceId === 1
    const newItem: FavouriteStudyFormItemState = {
      favouritestudyitem: null,
      nomenclatorid: null,
      nomenclatorcode: isNbu ? n.code ?? null : null,
      nomenclatordescription: isNbu ? n.shortdescription ?? n.description ?? null : null,
      code: n.code ?? null,
      description: n.shortdescription ?? n.description ?? null
    }

    const alreadyExists = addedStudies.some(study => {
      if (isNbu) {
        return (
          isNbuItem(study) &&
          (study.nomenclatorcode ?? "").trim() === (newItem.nomenclatorcode ?? "").trim()
        )
      }

      return (
        (study.code ?? "").trim() === (newItem.code ?? "").trim() &&
        (study.description ?? "").trim() ===
        (newItem.description ?? "").trim()
      )
    })

    if (alreadyExists) return

    setAddedStudies(prev => [...prev, newItem])
    setSelectedStudy(null)
  }

  const handleRemoveStudy = (index: number) => {
    setAddedStudies(prev => {
      const target = prev[index]
      const itemId = Number(target?.favouritestudyitem ?? 0)

      if (itemId > 0) {
        setRemovedStudyItemIds(curr =>
          curr.includes(itemId) ? curr : [...curr, itemId]
        )
      }

      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSave = async () => {
    if (isSaving) return

    if (!form.favouritestudydesc.trim() || !form.studytype) {
      dispatch(
        setNotification({
          title: "Campos requeridos",
          desc: "Completá al menos el nombre y tipo del estudio.",
          icon: "pi pi-exclamation-triangle",
          typeOfNotification: ETypeOfNotification.WARNING
        })
      )
      return
    }

    const incompatibleItems = addedStudies.filter(
      item => !isItemCompatibleWithSource(item, sourceId)
    )
    if (incompatibleItems.length > 0) {
      dispatch(
        setNotification({
          title: "Estudios incompatibles",
          desc: "Hay estudios cargados que no coinciden con el tipo seleccionado. Revisá la lista antes de guardar.",
          icon: "pi pi-exclamation-triangle",
          typeOfNotification: ETypeOfNotification.WARNING
        })
      )
      return
    }

    const payload = toSaveFullFavouriteStudyPayload(
      {
        ...form,
        ispublic: isAdministrativeOnlyUser ? true : form.ispublic
      },
      addedStudies,
      removedStudyItemIds
    )

    try {
      setIsSaving(true)

      const savedDto = await saveFullFavouriteStudy(payload)
      const mappedSaved = mapFullFavouriteStudyToForm(savedDto)

      setForm(mappedSaved.form)
      setAddedStudies(mappedSaved.items)
      setRemovedStudyItemIds([])

      let savedId =
        resolveFavouriteStudyId(savedDto) ??
        (form.favouritestudy > 0 ? form.favouritestudy : null)

      if (!savedId && !form.favouritestudy) {
        const confirmed = await findFavoriteStudyByDescription(
          payload.description,
          form.studytype
        )
        savedId = resolveFavouriteStudyId(confirmed)
      }

      if (!savedId) {
        throw new Error(
          "No se pudo confirmar el guardado en la respuesta del backend"
        )
      }

      if (savedDto.ispublic && !form.ispublic) {
        dispatch(
          setNotification({
            title: "Guardado como público",
            desc: "Tu usuario no tiene profesional asignado, por lo tanto la agrupación se creó como pública.",
            typeOfNotification: ETypeOfNotification.WARNING
          })
        )
      }

      dispatch(
        setNotification({
          title: "Operación exitosa",
          desc: "El estudio fue guardado correctamente",
          typeOfNotification: ETypeOfNotification.INFO
        })
      )

      onSaved?.()
      setVisible(false)
    } catch (e: unknown) {
      const errorInfo = getApiErrorInfo(e)
      const fallbackMessage =
        errorInfo.status && errorInfo.status >= 400
          ? `No se pudo guardar el estudio (HTTP ${errorInfo.status}).`
          : "No se pudo guardar el estudio."
      const uiMessage = getApiErrorMessage(e, fallbackMessage)

      if (!errorInfo.hasResponse) {
        console.error("saveFullFavouriteStudy sin response", {
          code: errorInfo.code,
          message: errorInfo.message,
          url: errorInfo.url,
          method: errorInfo.method
        })
      }

      if (!form.favouritestudy) {
        try {
          const confirmed = await findFavoriteStudyByDescription(
            payload.description,
            form.studytype
          )
          const confirmedId = resolveFavouriteStudyId(confirmed)

          if (confirmedId) {
            dispatch(
              setNotification({
                title: "Operación exitosa",
                desc: "La agrupación se creó correctamente. Se recuperó luego de una respuesta inconsistente.",
                typeOfNotification: ETypeOfNotification.WARNING
              })
            )
            onSaved?.()
            setVisible(false)
            return
          }
        } catch { }
      }

      dispatch(
        setNotification({
          title: "Error",
          desc: uiMessage,
          typeOfNotification: ETypeOfNotification.ERROR
        })
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      header={studyToEdit ? "Editar agrupación" : "Crear agrupación"}
      width="w-medium"
      className="crud-favorite-modal"
    >
      <div className="crud-form">
        <div className="flex justify-content-between align-items-center mb-2">
          <h3 className="text-lg font-semibold"></h3>

          <div className="flex align-items-center gap-2">
            <InputSwitch
              checked={form.active}
              onChange={e => setForm({ ...form, active: e.value })}
            />
            <span className="text-sm">
              {form.active ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>

        <div className="form-section">
          <label>Tipo de Estudio</label>

          <Combobox
            label="Tipo de Estudio *"
            value={selectedStudyTypeOption}
            setValue={option =>
              setForm({ ...form, studytype: option?.value ?? 0 })
            }
            getItems={async () =>
              studyTypes.map(t => ({
                label: t.i18ncodes?.[0]?.description ?? `Tipo ${t.studyType}`,
                value: t.studyType
              }))
            }
            optionLabel="label"
            className="w-full"
          />
        </div>

        <InputTextCustom
          label="Descripción"
          onChange={e =>
            setForm({ ...form, favouritestudydesc: e.target.value })
          }
          placeholder="Nombre de la agrupación"
          className="w-full form-section"
          value={form.favouritestudydesc}
        />

        <div className="form-section">
          {isAdministrativeOnlyUser ? (
            <>
              <label>Perfil de uso</label>
              <div className="text-sm text-gray-600">Esta configuración se guardará como pública.</div>
            </>
          ) : (
            <RadioButtonGroup
              label="Perfil de uso"
              value={form.ispublic ? "publico" : "personal"}
              setValue={v =>
                setForm({ ...form, ispublic: v === "publico" })
              }
              orientation="column"
              options={[
                { value: "personal", label: "Personal" },
                { value: "publico", label: "Público" }
              ]}
            />
          )}
        </div>

        <div className="separator-line"></div>

        <div className="form-section">
          <RadioButtonGroup
            label="Origen"
            value="nomenclador"
            setValue={() => { }}
            orientation="column"
            options={[
              {
                value: "nomenclador",
                label:
                  sourceId === 2
                    ? "Buscar concepto SNOMED"
                    : "Buscar práctica NBU"
              }
              // {
              //   value: "libre",
              //   label: "Ingresar texto libre"
              // }
            ]}
          />
        </div>

        <div className="form-section">
          {/* Texto libre deshabilitado temporalmente. */}
          {/* <label>
            {origin === "libre"
              ? "Indicar código + texto"
              : `Seleccionar estudio en ${sourceId === 2 ? "SNOMED" : "NBU"}`}
          </label> */}

          {
            // origin === "libre" ? (
            //   <>
            //     <InputText
            //       value={freeCode}
            //       onChange={e => setFreeCode(e.target.value)}
            //       placeholder="Código"
            //       maxLength={8}
            //       className="w-full mb-2"
            //     />
            //
            //     <InputText
            //       value={freeText}
            //       onChange={e => setFreeText(e.target.value)}
            //       placeholder="Descripción del estudio"
            //       className="w-full"
            //     />
            //   </>
            // ) : (
            <Combobox
              pageSizeParam={20}
              getItems={async ({ filterValue, offSet, pageSize }) => {
                const f = (filterValue ?? "").trim()

                if (sourceId === 2) {
                  const items = await searchTerminologySnomed(f, {
                    context: "PROCEDURE",
                    language: "es",
                    offSet: offSet ?? 0,
                    size: pageSize ?? 20,
                    preloadSize: pageSize ?? 20,
                    fallbackQuery: "estudio",
                    minQueryLength: 3
                  })

                  return items.map(item => ({
                    label: `${item.code} - ${item.description}`,
                    value: {
                      code: item.code,
                      description: item.description
                    }
                  }))
                }

                const items = await getNomenclatorCodes({
                  filterValue: f,
                  offSet: offSet ?? 0,
                  pageSize: pageSize ?? 20,
                  params: { nomenclatorsourceid: 1 }
                } as any)

                return items.map(item => ({
                  label: `${item.code} - ${item.description}`,
                  value: {
                    code: item.code,
                    shortdescription: item.description,
                    description: item.description
                  }
                }))
              }}
              optionLabel="label"
              value={selectedStudy}
              setValue={setSelectedStudy}
              className="w-full"
              placeholder={
                sourceId === 2
                  ? "Buscar por texto clínico o código SNOMED"
                  : "Buscar por código o descripción NBU"
              }
            />
            // )
          }

          <div className="form-section mt-2">
            <Button
              label="Agregar estudio"
              className="btn-secondary"
              onClick={handleAddStudy}
              disabled={!selectedStudy}
            // disabled={
            //   origin === "libre"
            //     ? !freeCode.trim() || !freeText.trim()
            //     : !selectedStudy
            // }
            />
          </div>
        </div>

        <div className="form-section">
          <label>Estudios agregados ({addedStudies.length})</label>
          <div className="added-list">
            {addedStudies.length === 0 && (
              <div className="text-sm text-gray-500">
                Sin estudios cargados
              </div>
            )}

            {(addedStudies ?? []).map((s, idx) => (
              <div key={idx} className="added-item">
                <div className="flex flex-column">
                  <span className="font-semibold">
                    {s.code ?? s.nomenclatorcode ?? "-"}
                  </span>
                  <span className="text-sm">
                    {s.description ?? s.nomenclatordescription ?? "-"}
                  </span>
                </div>

                <Button
                  // Luego utilizar SelectableList para mejorar la presentación y usar el framework
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger "
                  onClick={() => handleRemoveStudy(idx)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="actions">
          <Button
            label={studyToEdit ? "Guardar" : "Crear"}
            className="btn-primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving}
          />
          <Button
            label="Cancelar"
            className="btn-secondary"
            onClick={() => setVisible(false)}
            disabled={isSaving}
          />
        </div>
      </div>
    </Modal>
  )
}
